/**
 * `mod.docs`: what a fill can see of your documents (RFC 0284 S5c).
 *
 * WHY THIS EXISTS. S2 gave a fill the window, which says which panes
 * are OPEN. That is enough to arrange things and not enough to be a sidebar: a
 * sidebar's whole job is to show you what you have so you can open it. Asked
 * for "a sidebar that lists my documents", the contract could not answer, which
 * is the RFC's own open question arriving in practice.
 *
 * READ ONLY, deliberately. Opening a document is `window.open`, which already
 * exists and already ends in `normalize`. Adding an open here would be a second
 * way to do one thing, and the second way is the one that skips the rules.
 * Creating, renaming and deleting are the document capability apps already have
 * (RFC 0267), disclosed at install; chrome does not get them for free by being
 * chrome.
 *
 * A PROJECTION, not the catalog. The same rows `projectDocuments` gives the
 * built-in sidebar: id, title, path, mime, when it changed, and the data type
 * if it is an app's record. No handles and no content, so a fill cannot
 * reach past what it was given, and listing a thousand documents hydrates none
 * of them.
 */

/** One document, as a fill sees it. */
export interface DocView {
  id: string
  /**
   * The project it belongs to.
   *
   * Carried because `window.open` REFUSES a document with no project, and a
   * fill has no other way to learn one. Without it every row in a
   * custom sidebar would be unclickable, which is the worst kind of broken:
   * it draws correctly and does nothing.
   */
  projectId: string
  title: string
  path: string
  mime: string
  updatedAt: number
  /** Set when the document is an app's record rather than plain prose. */
  dataType?: string
}

export interface DocsCapability {
  /** Every document in this project, most recently changed first. */
  list(): DocView[]
  /** Fires when that list changes. Returns an unsubscribe. */
  onChange(fn: () => void): () => void
}

export interface DocsDeps {
  documents: () => DocView[]
  subscribe: (fn: () => void) => () => void
}

export function makeDocs(deps: DocsDeps): DocsCapability {
  return {
    // COPIED on the way out. The array the shell holds is rebuilt from the
    // project document, and handing it over directly would let a
    // fill sort it in place and reorder the built-in sidebar with it.
    list: () => deps.documents().map((d) => ({ ...d })),
    onChange: (fn) => deps.subscribe(fn),
  }
}
